import { useQuery } from '@tanstack/react-query'
import { Megaphone } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/Skeleton'
import { Switch } from '@/components/ui/Switch'
import { Textarea } from '@/components/ui/Textarea'
import { settingsQuery, useSaveReenrollmentSettings } from '../api'
import type { ReenrollmentSettings } from '../types'

function SettingsForm({ settings, canManage }: { settings: ReenrollmentSettings; canManage: boolean }) {
  const { t } = useTranslation('promotion')
  const [isOpen, setOpen] = useState(settings.isOpen)
  const [deadline, setDeadline] = useState(settings.deadline ?? '')
  const [message, setMessage] = useState(settings.message ?? '')
  const save = useSaveReenrollmentSettings()

  const submit = () =>
    save.mutate(
      { toYearId: settings.toYearId, isOpen, deadline: deadline || null, message: message.trim() || null },
      {
        onSuccess: () => toast.success(t('Re-enrolment settings saved')),
        onError: (e) => toast.error(t('Could not save'), errorMessage(e)),
      },
    )

  return (
    <CardBody className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <span>
          <span id="reenrollment-open" className="block text-sm font-medium text-ink">
            {t('Families can answer online')}
          </span>
          <span className="block text-xs text-muted">
            {t('Parents and students see a re-enrolment card in their portal.')}
          </span>
        </span>
        <Switch
          checked={isOpen}
          onCheckedChange={setOpen}
          disabled={!canManage}
          aria-labelledby="reenrollment-open"
        />
      </div>
      <FormField label={t('Deadline')} hint={t('Optional. Answers are refused after this date.')}>
        <Input
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          disabled={!canManage}
        />
      </FormField>
      <FormField
        label={t('Message to families')}
        hint={t('For example the fees and where to pay them (at the school office).')}
      >
        <Textarea
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={!canManage}
        />
      </FormField>
      {canManage ? (
        <div className="flex justify-end">
          <Button onClick={submit} loading={save.isPending}>
            {t('Save settings')}
          </Button>
        </div>
      ) : null}
    </CardBody>
  )
}

/** Open or close re-enrolment for the next year, with a deadline and a message for families. */
export function ReenrollmentSettingsPanel({ toYearId, canManage }: { toYearId: number; canManage: boolean }) {
  const { t } = useTranslation('promotion')
  const { data } = useQuery(settingsQuery(toYearId))
  return (
    <Card>
      <CardHeader
        icon={<Megaphone />}
        title={t('Family re-enrolment')}
        actions={
          data ? (
            <Badge tone={data.acceptsAnswers ? 'success' : 'neutral'} dot>
              {data.acceptsAnswers ? t('Open') : t('Closed')}
            </Badge>
          ) : null
        }
      />
      {data ? (
        <SettingsForm
          key={`${data.toYearId}-${String(data.isOpen)}-${data.deadline ?? ''}`}
          settings={data}
          canManage={canManage}
        />
      ) : (
        <CardBody>
          <Skeleton className="h-24 w-full" />
        </CardBody>
      )}
    </Card>
  )
}
